import { useRouter } from 'next/router';
import { useStage } from '@/components/DialogStage';
import { DynamicLink } from '@/components/DynamicLink';
import { conversionPoints } from '@/components/Modals/context/ModalRoutingContext';
import { ModalConversionPoints } from '@/components/Modals/shared-types';
import { Button } from '@music-vine/cadence/ui';
import { useHasRole, useIsLoggedIn } from '@/hooks/auth';
import { useSubscribeModal } from '@/hooks/billing/useSubscribeModal';
import { recordConversion } from '@/utils/analytics/conversion';
import { usesHomepageNav } from '@/utils/page-chrome';
import RemainingCredits from './RemainingCredits';
import SafelistYourChannel from './SafelistYourChannel';
import { TopBarContentBase } from './TopBarContentBase';
import UserMenu from './UserMenu';
interface TopBarContentProps {
  children?: React.ReactNode;
}

/**
 * The order and items to expect on all certain screen sizes from left to right:
 *
 * Components in brackets [] retain their position on the given breakpoint.
 *
 * Mobile:
 * - Visitor: [Logo,] Sign up, Log in, [Search button, General menu]
 * - Free user: [Logo,] Subscribe now, Account menu, [Search button, General menu]
 * - Subscriber: [Logo,] Safelist your channel | Safelisted channels, Account menu, [Search button, General menu]
 *
 *
 * Desktop:
 * - Visitor: [Logo, Global search,] Sign up, Log in
 * - Free user: [Logo, Global search,] Safelist your channel, remaining credits, Pricing, Subscribe now, Account menu
 * - Subscriber: [Logo, Global search,] Safelist your channel | Safelisted channels, Account menu
 *
 * Homepage:
 * - Adds library links (Music, Sound effects[, Stock video], Motion graphics, LUTs) before other content
 */
export const TopBarContent = ({
  children
}: TopBarContentProps) => {
  const router = useRouter();
  const userIsLoggedIn = useIsLoggedIn();
  const {
    open: openStage
  } = useStage();
  const {
    openSubscribeModalWithConversion
  } = useSubscribeModal();
  const isFree = useHasRole('Free');
  const showLibraryLinks = usesHomepageNav(router.pathname);
  return <TopBarContentBase data-sentry-element="TopBarContentBase" data-sentry-component="TopBarContent" data-sentry-source-file="TopBarContent.tsx">
      {children}

      {showLibraryLinks && <div className="mr-6 hidden items-center gap-2 transition-opacity duration-200 ease-out focus-within:opacity-100! hover:opacity-100 group-[.bg-transparent]:pointer-events-none group-[.bg-transparent]:opacity-0 md:flex">
          <Button asChild className="font-normal focus:bg-white dark:focus:bg-gray-900" fontSize={{
        lg: 'sm'
      }} size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} variant="transparent">
            <DynamicLink href="/music">Music</DynamicLink>
          </Button>
          <Button asChild className="font-normal focus:bg-white dark:focus:bg-gray-900" fontSize={{
        lg: 'sm'
      }} size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} variant="transparent">
            <DynamicLink aria-label="Sound effects" href="/sfx">
              <span aria-hidden="true" className="lg:hidden">
                SFX
              </span>
              <span className="hidden lg:inline">Sound effects</span>
            </DynamicLink>
          </Button>
          <Button asChild className="font-normal focus:bg-white dark:focus:bg-gray-900" fontSize={{
        lg: 'sm'
      }} size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} variant="transparent">
            <DynamicLink aria-label="Stock video" href="/stock-video">
              <span aria-hidden="true" className="lg:hidden">
                Videos
              </span>
              <span className="hidden lg:inline">Stock video</span>
            </DynamicLink>
          </Button>
          <Button asChild className="font-normal focus:bg-white dark:focus:bg-gray-900" fontSize={{
        lg: 'sm'
      }} size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} variant="transparent">
            <DynamicLink aria-label="Motion graphics" href="/motion-graphics">
              <span aria-hidden="true" className="lg:hidden">
                Motion
              </span>
              <span className="hidden lg:inline">Motion graphics</span>
            </DynamicLink>
          </Button>
          <Button asChild className="font-normal focus:bg-white dark:focus:bg-gray-900" fontSize={{
        lg: 'sm'
      }} size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} variant="transparent">
            <DynamicLink href="/luts">LUTs</DynamicLink>
          </Button>
        </div>}
      <SafelistYourChannel data-sentry-element="SafelistYourChannel" data-sentry-source-file="TopBarContent.tsx" />
      {isFree && <RemainingCredits className="hidden xxs:hidden md:flex" />}
      {(isFree || !userIsLoggedIn) && <Button asChild className="hidden md:flex" fontSize={{
      lg: 'sm'
    }} size={{
      default: 'sm',
      lg: 'default',
      xl: 'lg'
    }} variant="light">
          <DynamicLink href="/pricing">Pricing</DynamicLink>
        </Button>}
      {isFree && userIsLoggedIn && <Button className="btn-go-premium" fontSize={{
      lg: 'sm'
    }} onClick={() => openSubscribeModalWithConversion(ModalConversionPoints.Header)} size={{
      default: 'sm',
      lg: 'default',
      xl: 'lg'
    }} variant="brand">
          Subscribe
        </Button>}
      {!userIsLoggedIn && <>
          <Button className="btn-sign-up" fontSize={{
        lg: 'sm'
      }} onClick={() => {
        recordConversion({
          source: conversionPoints[ModalConversionPoints.SignUp]
        });
        openStage('auth.signup-options');
      }} size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} variant="brand">
            Sign up
          </Button>
          <Button className="btn-log-in" fontSize={{
        lg: 'sm'
      }} onClick={() => {
        openStage('auth.login');
      }} size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} variant="light">
            Log in
          </Button>
        </>}
      {userIsLoggedIn && <UserMenu />}
    </TopBarContentBase>;
};